import Vue from 'vue'
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser'

const clientId = process.env.VUE_APP_AAD_CLIENT_ID
const apiScope = `api://${clientId}/access_as_user`

// Match vue-msal request.scopes for API access; OIDC scopes for the login round-trip.
// Requesting only the API scope during loginRedirect is brittle on msal-browser 2.x.
const loginRequest = {
  scopes: ['openid', 'profile', 'offline_access'],
  extraScopesToConsent: [apiScope],
}

const tokenRequest = {
  scopes: [apiScope],
}

const msalInstance = new PublicClientApplication({
  auth: {
    clientId,
    authority: process.env.VUE_APP_AAD_AUTHORITY,
    redirectUri: process.env.VUE_APP_AAD_REDIRECT_URL,
    postLogoutRedirectUri: process.env.VUE_APP_AAD_REDIRECT_URL,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  },
})

let initPromise = null
let lastLoginError = null

const LEGACY_MSAL_KEYS = [
  'msal.idtoken',
  'msal.client.info',
  'msal.session.state',
  'msal.error',
  'msal.error.description',
  'msal.token.renew.name',
  'msal.nonce.id_token',
  'msal.state.login',
  'msal.state.acquireToken',
  'msal.acquireToken.request',
  'msal.login.request',
]

function clearLegacyMsalV1Cache() {
  try {
    LEGACY_MSAL_KEYS.forEach((key) => localStorage.removeItem(key))
    const toRemove = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (!key) continue
      // Old msal@1 access-token entries used by the previous http.js scanner
      if (key.indexOf(`api://${clientId}/access_as_user`) >= 0) {
        toRemove.push(key)
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key))
  } catch (e) {
    // ignore storage errors
  }
}

function mapUser(account) {
  if (!account) {
    return null
  }
  return {
    name: account.name || account.username,
    userName: account.username,
    idTokenClaims: account.idTokenClaims,
  }
}

function getAccount() {
  const active = msalInstance.getActiveAccount()
  if (active) {
    return active
  }
  const accounts = msalInstance.getAllAccounts()
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0])
    return accounts[0]
  }
  return null
}

function formatAuthError(error) {
  if (!error) {
    return 'login_failed'
  }
  if (typeof error === 'string') {
    return error
  }
  const code = error.errorCode || error.code || ''
  const message = error.message || error.errorMessage || String(error)
  return code ? `${code}: ${message}` : message
}

function storeLoginError(error) {
  lastLoginError = error
  localStorage.setItem('msal.login.error', formatAuthError(error))
  // Keep the real AAD / MSAL reason visible for support
  // eslint-disable-next-line no-console
  console.error('[msal] login/redirect failed:', error)
}

function clearLoginError() {
  lastLoginError = null
  localStorage.removeItem('msal.login.error')
}

async function acquireApiToken(account) {
  const result = await msalInstance.acquireTokenSilent({
    account,
    scopes: tokenRequest.scopes,
  })
  return result.accessToken
}

async function ensureReady() {
  if (!initPromise) {
    initPromise = (async () => {
      clearLegacyMsalV1Cache()

      // msal-browser v3+ requires initialize(); v2.x no-ops without it.
      if (typeof msalInstance.initialize === 'function') {
        await msalInstance.initialize()
      }

      try {
        const result = await msalInstance.handleRedirectPromise()
        if (result && result.account) {
          msalInstance.setActiveAccount(result.account)
          clearLoginError()
          // vue-msal always acquired the API token right after login
          try {
            await acquireApiToken(result.account)
          } catch (tokenError) {
            if (tokenError instanceof InteractionRequiredAuthError) {
              await msalInstance.acquireTokenRedirect({
                account: result.account,
                scopes: tokenRequest.scopes,
              })
              return
            }
            storeLoginError(tokenError)
          }
        } else if (getAccount()) {
          // Returning visitor / soft reload after successful redirect navigation
          clearLoginError()
        }
      } catch (error) {
        // Ignore "interaction in progress" noise from a second tab/load
        if (
          error instanceof BrowserAuthError &&
          error.errorCode === 'interaction_in_progress'
        ) {
          return
        }
        storeLoginError(error)
      }
    })()
  }
  return initPromise
}

const auth = {
  ready: ensureReady,
  getLastLoginError() {
    return lastLoginError || localStorage.getItem('msal.login.error')
  },
  getLastLoginErrorText() {
    return formatAuthError(auth.getLastLoginError())
  },
  clearLoginError,
  isAuthenticated() {
    return !!getAccount()
  },
  getUser() {
    return mapUser(getAccount())
  },
  async signIn() {
    await ensureReady()
    clearLoginError()
    return msalInstance.loginRedirect(loginRequest)
  },
  async signOut() {
    await ensureReady()
    const account = getAccount()
    return msalInstance.logoutRedirect({
      account: account || undefined,
    })
  },
  async getAccessToken() {
    await ensureReady()
    const account = getAccount()
    if (!account) {
      return null
    }
    try {
      return await acquireApiToken(account)
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect({
          account,
          scopes: tokenRequest.scopes,
        })
        return null
      }
      throw error
    }
  },
}

Vue.prototype.$msal = auth

export default auth
